import { Node, mergeAttributes } from '@tiptap/core';
import { ReactNodeViewRenderer, NodeViewWrapper } from '@tiptap/react';
import React, { useState } from 'react';
import { Copy, Minimize2, Maximize2, Trash2, Layout } from 'lucide-react';
// eslint-disable-next-line no-restricted-imports
import { toastSink } from '../../../../shared/ui/Toast/toastSink';

const InlineFrameBlockView = ({ node, updateAttributes, extension, deleteNode }) => {
    const { url, isCompressed, createdBy, id } = node.attrs;
    const { currentUser } = extension.options;
    const [inputUrl, setInputUrl] = useState('');
    const [isEditingUrl, setIsEditingUrl] = useState(false);

    const handleSave = () => {
        if (!inputUrl.trim()) return;
        let finalUrl = inputUrl.trim();
        if (!/^https?:\/\//i.test(finalUrl)) finalUrl = 'https://' + finalUrl;
        updateAttributes({ url: finalUrl });
        toastSink.success('URL del frame guardada');
    };

    const handleCopy = () => {
        if (url) {
            navigator.clipboard.writeText(url);
            toastSink.success('URL copiada al portapapeles');
        }
    };

    const toggleCompress = () => {
        updateAttributes({ isCompressed: !isCompressed });
    };

    const handleDelete = () => {
        toastSink.prompt('¿Estás seguro que deseas eliminar este frame?', {
            confirmText: 'Eliminar',
            cancelText: 'Cancelar',
            onConfirm: () => {
                deleteNode();
                toastSink.success('Frame eliminado');
            }
        });
    };

    const canDelete = currentUser && (currentUser.id === createdBy || currentUser.role === 'admin');

    return (
        <NodeViewWrapper className="doc-inline-frame-node-wrapper" data-id={id} style={{ margin: '24px 0' }}>
            <div 
                className="doc-inline-frame-block" 
                contentEditable={false} 
                suppressContentEditableWarning
                style={{
                    border: url ? '1px solid rgba(255,255,255,0.1)' : '1px dashed #666',
                    borderRadius: '8px',
                    overflow: 'hidden',
                    backgroundColor: 'rgba(0,0,0,0.2)'
                }}
            >
                {!url ? (
                    <div style={{ padding: '24px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '12px' }}>
                        <Layout size={24} color="#888" />
                        <div style={{ color: '#aaa', fontSize: '14px' }}>Pega el URL del frame a proyectar</div>
                        <div style={{ display: 'flex', gap: '8px', width: '100%', maxWidth: '400px' }}>
                            <input 
                                type="text"
                                value={inputUrl}
                                onChange={(e) => setInputUrl(e.target.value)}
                                placeholder="https://ejemplo.com"
                                style={{
                                    flex: 1, padding: '8px 12px', borderRadius: '6px',
                                    border: '1px solid rgba(255,255,255,0.2)', background: 'rgba(255,255,255,0.05)',
                                    color: 'white', outline: 'none'
                                }}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter') handleSave();
                                }}
                            />
                            <button 
                                onClick={handleSave}
                                style={{
                                    padding: '8px 16px', background: '#3b82f6', color: 'white',
                                    border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: '500'
                                }}
                            >
                                Guardar
                            </button>
                        </div>
                    </div>
                ) : (
                    <>
                        {/* Header minimalista */}
                        <div style={{ 
                            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                            padding: '6px 12px', background: 'rgba(255,255,255,0.05)', borderBottom: '1px solid rgba(255,255,255,0.1)'
                        }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', overflow: 'hidden', flex: 1 }}>
                                {isEditingUrl ? (
                                    <input
                                        type="text"
                                        defaultValue={url}
                                        autoFocus
                                        onBlur={(e) => {
                                            setIsEditingUrl(false);
                                            let val = e.target.value.trim();
                                            if (val && !/^https?:\/\//i.test(val)) val = 'https://' + val;
                                            if (val && val !== url) {
                                                updateAttributes({ url: val });
                                                toastSink.success('URL actualizada');
                                            }
                                        }}
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter') e.target.blur();
                                            if (e.key === 'Escape') setIsEditingUrl(false);
                                        }}
                                        style={{
                                            flex: 1, padding: '4px 8px', fontSize: '12px', borderRadius: '4px',
                                            border: '1px solid rgba(255,255,255,0.2)', background: 'rgba(255,255,255,0.05)',
                                            color: 'white', outline: 'none'
                                        }}
                                    />
                                ) : (
                                    <span 
                                        onDoubleClick={() => setIsEditingUrl(true)}
                                        title="Doble click para editar"
                                        style={{ fontSize: '12px', color: '#888', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '300px', cursor: 'text' }}
                                    >
                                        {url}
                                    </span>
                                )}
                                {!isEditingUrl && (
                                    <button onClick={handleCopy} title="Copiar URL" style={{ background: 'transparent', border: 'none', cursor: 'pointer', display: 'flex', padding: '4px' }}>
                                        <Copy size={12} color="#888" />
                                    </button>
                                )}
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                <button onClick={toggleCompress} title={isCompressed ? 'Descomprimir' : 'Comprimir (400px)'} style={{ background: 'transparent', border: 'none', cursor: 'pointer', display: 'flex', padding: '4px' }}>
                                    {isCompressed ? <Maximize2 size={14} color="#888" /> : <Minimize2 size={14} color="#888" />}
                                </button>
                                {canDelete && (
                                    <button onClick={handleDelete} title="Eliminar Frame" style={{ background: 'transparent', border: 'none', cursor: 'pointer', display: 'flex', padding: '4px' }}>
                                        <Trash2 size={14} color="#ef4444" />
                                    </button>
                                )}
                            </div>
                        </div>
                        {/* Iframe */}
                        <iframe 
                            src={url} 
                            style={{ 
                                width: '100%', 
                                height: isCompressed ? '400px' : '70vh', 
                                border: 'none', 
                                display: 'block',
                                minHeight: isCompressed ? '400px' : '600px'
                            }} 
                            title="Inline Frame"
                            sandbox="allow-scripts allow-same-origin allow-popups allow-forms"
                        />
                    </>
                )}
            </div>
        </NodeViewWrapper>
    );
};

export const InlineFrameExtension = Node.create({
    name: 'inlineFrame',
    group: 'block',
    atom: true,
    selectable: true,
    draggable: true,

    addOptions() {
        return {
            currentUser: null,
        };
    },

    addAttributes() {
        return {
            id: {
                default: null,
                parseHTML: element => element.getAttribute('data-id') || null,
                renderHTML: attributes => (attributes.id ? { 'data-id': attributes.id } : {}),
            },
            url: {
                default: '',
                parseHTML: element => element.getAttribute('data-url') || '',
                renderHTML: attributes => (attributes.url ? { 'data-url': attributes.url } : {}),
            },
            isCompressed: {
                default: false,
                parseHTML: element => element.getAttribute('data-is-compressed') === 'true',
                renderHTML: attributes => ({ 'data-is-compressed': String(attributes.isCompressed) }),
            },
            createdBy: {
                default: null,
                parseHTML: element => element.getAttribute('data-created-by') || null,
                renderHTML: attributes => (attributes.createdBy ? { 'data-created-by': attributes.createdBy } : {}),
            },
        };
    },

    parseHTML() {
        return [
            {
                tag: 'div.doc-inline-frame-block',
                getAttrs: element => ({
                    id: element.getAttribute('data-id') || null,
                    url: element.getAttribute('data-url') || '',
                    isCompressed: element.getAttribute('data-is-compressed') === 'true',
                    createdBy: element.getAttribute('data-created-by') || null,
                }),
            },
        ];
    },

    renderHTML({ HTMLAttributes }) {
        return [
            'div',
            mergeAttributes({ class: 'doc-inline-frame-block', contenteditable: 'false' }, HTMLAttributes),
        ];
    },

    addNodeView() {
        return ReactNodeViewRenderer(InlineFrameBlockView);
    },
});
